import { useEffect, useState } from 'react';

import {
  Button,
  Center,
  Flex,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Select,
  Spinner,
  Text,
} from '@chakra-ui/react';

import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';

import useFetchFileDownloadUrl from 'app/hooks/useFetchFileDownloadUrl';

import { CategoryImageLightboxProps } from './types';

/**
 * Full-size viewer for a category's images. Opens on the clicked image and lets the user page
 * left/right through every image. When assignableCategories + onAssign are provided, it also offers
 * assigning the current image to one of those categories (scoped by the caller to global or a
 * specific company).
 */
function CategoryImageLightbox({
  isOpen,
  images,
  startIndex,
  onClose,
  assignableCategories,
  onAssign,
}: CategoryImageLightboxProps) {
  const [index, setIndex] = useState(startIndex);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');

  useEffect(() => {
    if (isOpen) {
      setIndex(startIndex);
    }
  }, [isOpen, startIndex]);

  useEffect(() => {
    setSelectedCategoryId('');
  }, [index, isOpen]);

  const current = images[index];
  const { data: imageUrl, isInitialLoading } = useFetchFileDownloadUrl(
    current?.imageId,
    current?.fileName
  );

  const hasMultiple = images.length > 1;
  const goPrevious = () => setIndex((value) => (value - 1 + images.length) % images.length);
  const goNext = () => setIndex((value) => (value + 1) % images.length);

  const canAssign = !!onAssign && !!assignableCategories && assignableCategories.length > 0;

  const handleAssign = () => {
    if (onAssign && current && selectedCategoryId) {
      onAssign(selectedCategoryId, current.imageId);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody py="24px">
          <Flex align="center" gap="12px">
            <IconButton
              aria-label="Previous image"
              icon={<ChevronLeftIcon boxSize="28px" />}
              variant="ghost"
              isDisabled={!hasMultiple}
              onClick={goPrevious}
            />
            <Center
              flex="1"
              minH="360px"
              bg="brand.lightGray2"
              borderRadius="10px"
              overflow="hidden"
            >
              {isInitialLoading || !imageUrl ? (
                <Spinner color="brand.purple" size="lg" />
              ) : (
                <Image
                  src={imageUrl}
                  alt={current?.fileName ?? 'Savings preset'}
                  maxH="60vh"
                  maxW="full"
                  objectFit="contain"
                />
              )}
            </Center>
            <IconButton
              aria-label="Next image"
              icon={<ChevronRightIcon boxSize="28px" />}
              variant="ghost"
              isDisabled={!hasMultiple}
              onClick={goNext}
            />
          </Flex>

          <Text mt="12px" textAlign="center" textStyle="smallRegular" color="brand.gray">
            {images.length > 0 ? `${index + 1} / ${images.length}` : ''}
            {current?.fileName ? ` — ${current.fileName}` : ''}
          </Text>

          {canAssign && (
            <Flex mt="16px" align="center" justify="center" gap="8px">
              <Select
                placeholder="Assign to category…"
                value={selectedCategoryId}
                onChange={(event) => setSelectedCategoryId(event.target.value)}
                maxW="260px"
                data-testid="assign-category-select"
              >
                {assignableCategories?.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
              <Button onClick={handleAssign} isDisabled={!selectedCategoryId}>
                Assign
              </Button>
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default CategoryImageLightbox;
